import { useTranslation } from "react-i18next"

import type { ReasoningPreset, ReasoningPresets } from "@/features/chat/utils/reasoning-presets"
import type { ReasoningEffortOverride, ReasoningMode } from "@/stores/ai-explorer-store"

import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

const PRESET_EFFORTS: ReasoningEffortOverride[] = [
  "none",
  "minimal",
  "low",
  "medium",
  "high",
  "xhigh",
  "max",
]

const EDITABLE_PRESETS: Array<{ key: Exclude<ReasoningMode, "auto">; labelDefault: string }> = [
  { key: "fast", labelDefault: "Fast (/fast)" },
  { key: "thinking", labelDefault: "Thinking (/thinking)" },
  { key: "big_brain", labelDefault: "Big Brain (/bigbrain)" },
]

type AdminModel = { id: string; displayName?: string }

// Select can't hold a null value; this sentinel stands for "no minimum".
const NO_FLOOR = "__none__"

type EditExplorerReasoningPresetsProps = {
  models: AdminModel[]
  modelRoutingEnabled: boolean
  modelsLoading: boolean
  reasoningPresets: ReasoningPresets
  onUpdatePreset: (key: ReasoningMode, patch: Partial<ReasoningPreset>) => void
}

export function EditExplorerReasoningPresets({
  models,
  modelRoutingEnabled,
  modelsLoading,
  reasoningPresets,
  onUpdatePreset,
}: EditExplorerReasoningPresetsProps) {
  const { t } = useTranslation()
  const autoPreset = reasoningPresets.auto
  const autoModel = models.find((model) => model.id === autoPreset.model_id)
  const autoModelMissingFromCatalog =
    autoPreset.model_id !== "auto" && !models.some((model) => model.id === autoPreset.model_id)
  const catalogAutoLabel = t("adminDashboard.features.explorerFastRouter.presetAutoCatalog", {
    defaultValue: "Auto (catalog default)",
  })

  return (
    <div className="space-y-3 rounded-md border p-3">
      <div className="space-y-0.5">
        <Label>
          {t("adminDashboard.features.explorerFastRouter.presetsLabel", {
            defaultValue: "Reasoning presets",
          })}
        </Label>
        <p className="text-muted-foreground text-xs">
          {t("adminDashboard.features.explorerFastRouter.presetsHelp", {
            defaultValue:
              "/fast, /thinking and /bigbrain lock a model and effort. Auto (/auto, the default) uses the model and minimum effort below.",
          })}
        </p>
      </div>

      <div className="space-y-2 rounded-md border p-2.5">
        <div className="text-sm font-medium">
          {t("adminDashboard.features.explorerFastRouter.presetAuto", {
            defaultValue: "Auto (/auto)",
          })}
        </div>
        <p className="text-muted-foreground text-xs">
          {t("adminDashboard.features.explorerFastRouter.presetAutoHelp", {
            defaultValue:
              "Auto picks the effort for each message: low for ordinary questions, medium for code, attached files or explicitly complex requests. With Auto model selection on, the router may choose up to high; xhigh and max only when the user asks for them. The minimum effort raises anything below it. A user's effort access or plan limit can still cap it.",
          })}
        </p>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label className="text-muted-foreground text-xs">
              {t("adminDashboard.features.explorerFastRouter.presetModel", {
                defaultValue: "Model",
              })}
            </Label>
            <Select
              disabled={modelsLoading}
              value={autoPreset.model_id}
              onValueChange={(value) => onUpdatePreset("auto", { model_id: value })}
            >
              <SelectTrigger>
                <SelectValue>
                  {autoPreset.model_id === "auto"
                    ? catalogAutoLabel
                    : (autoModel?.displayName ?? autoPreset.model_id)}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem textValue={catalogAutoLabel} value="auto">
                  <div className="flex flex-col">
                    <span>{catalogAutoLabel}</span>
                    <span className="text-muted-foreground text-xs">
                      {t("adminDashboard.features.explorerFastRouter.presetAutoCatalogHelp", {
                        defaultValue: "The model the Auto entry points to in Models",
                      })}
                    </span>
                  </div>
                </SelectItem>
                {autoModelMissingFromCatalog && (
                  <SelectItem textValue={autoPreset.model_id} value={autoPreset.model_id}>
                    <div className="flex flex-col">
                      <span>{autoPreset.model_id}</span>
                      <span className="text-muted-foreground text-xs">
                        {t("adminDashboard.features.explorerFastRouter.presetModelMissing", {
                          defaultValue: "Not in current catalog — pick another or keep",
                        })}
                      </span>
                    </div>
                  </SelectItem>
                )}
                {models.map((model) => (
                  <SelectItem
                    key={model.id}
                    textValue={model.displayName ?? model.id}
                    value={model.id}
                  >
                    <div className="flex flex-col">
                      <span>{model.displayName ?? model.id}</span>
                      <span className="text-muted-foreground text-xs">{model.id}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {modelRoutingEnabled && (
              <p className="text-muted-foreground text-xs">
                {t("adminDashboard.features.explorerFastRouter.presetAutoRoutingNote", {
                  defaultValue:
                    "Auto model selection is on: the router's pick wins, and this model is used when it doesn't pick one.",
                })}
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label className="text-muted-foreground text-xs">
              {t("adminDashboard.features.explorerFastRouter.presetAutoFloor", {
                defaultValue: "Minimum effort",
              })}
            </Label>
            <Select
              value={autoPreset.effort ?? NO_FLOOR}
              onValueChange={(value) =>
                onUpdatePreset("auto", {
                  effort: value === NO_FLOOR ? null : (value as ReasoningEffortOverride),
                })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_FLOOR}>
                  {t("adminDashboard.features.explorerFastRouter.presetAutoNoFloor", {
                    defaultValue: "None — Auto decides",
                  })}
                </SelectItem>
                {PRESET_EFFORTS.map((effort) => (
                  <SelectItem key={effort} value={effort}>
                    {effort}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {EDITABLE_PRESETS.map(({ key, labelDefault }) => {
        const preset = reasoningPresets[key]
        const presetModel = models.find((model) => model.id === preset.model_id)
        const modelMissingFromCatalog = Boolean(
          preset.model_id && !models.some((model) => model.id === preset.model_id)
        )
        return (
          <div key={key} className="space-y-2 rounded-md border p-2.5">
            <div className="text-sm font-medium">
              {t(`adminDashboard.features.explorerFastRouter.preset_${key}`, {
                defaultValue: labelDefault,
              })}
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-muted-foreground text-xs">
                  {t("adminDashboard.features.explorerFastRouter.presetModel", {
                    defaultValue: "Model",
                  })}
                </Label>
                <Select
                  disabled={modelsLoading}
                  value={preset.model_id}
                  onValueChange={(value) => onUpdatePreset(key, { model_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue>{presetModel?.displayName ?? preset.model_id}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {modelMissingFromCatalog && (
                      <SelectItem textValue={preset.model_id} value={preset.model_id}>
                        <div className="flex flex-col">
                          <span>{preset.model_id}</span>
                          <span className="text-muted-foreground text-xs">
                            {t("adminDashboard.features.explorerFastRouter.presetModelMissing", {
                              defaultValue: "Not in current catalog — pick another or keep",
                            })}
                          </span>
                        </div>
                      </SelectItem>
                    )}
                    {models.map((model) => (
                      <SelectItem
                        key={model.id}
                        textValue={model.displayName ?? model.id}
                        value={model.id}
                      >
                        <div className="flex flex-col">
                          <span>{model.displayName ?? model.id}</span>
                          <span className="text-muted-foreground text-xs">{model.id}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-muted-foreground text-xs">
                  {t("adminDashboard.features.explorerFastRouter.presetEffort", {
                    defaultValue: "Effort",
                  })}
                </Label>
                <Select
                  value={preset.effort ?? "medium"}
                  onValueChange={(value) =>
                    onUpdatePreset(key, { effort: value as ReasoningEffortOverride })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRESET_EFFORTS.map((effort) => (
                      <SelectItem key={effort} value={effort}>
                        {effort}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
